import type { ProfileLink, ProfileLinks as ProfileLinksBlock } from '@ni/shared';
import { useMayWrite } from '../../api/app-context';
import { axisOfWrite } from '../../api/client';
import { useProfileLink } from '../../api/hooks';
import { formatDay } from '../../lib/format';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import { IfWrites } from '../write-access';
import './profile-links.css';

const PLATFORM_NAMES: Readonly<Record<ProfileLink['platform'], string>> = {
  x: 'X',
  bluesky: 'Bluesky',
  wallet: 'Wallet',
  onchain_entity: 'On-chain identity',
};

const isWallet = (link: ProfileLink): boolean => link.platform === 'wallet';

/** Social accounts, then the on-chain identity, then wallets; first-seen order within each. */
const PLATFORM_ORDER: Readonly<Record<ProfileLink['platform'], number>> = {
  x: 0,
  bluesky: 0,
  onchain_entity: 1,
  wallet: 2,
};
const grouped = (links: readonly ProfileLink[]): ProfileLink[] =>
  [...links].sort((a, b) => PLATFORM_ORDER[a.platform] - PLATFORM_ORDER[b.platform]);

/**
 * What the link is, in the office's words: "Dogecoin wallet" from the chain the
 * note named, "Wallet" when it named none, else the platform's own name. No chain
 * list: a chain is printed as the source wrote it.
 */
function kindOf(link: ProfileLink): string {
  if (isWallet(link) && link.chain) {
    const chain = link.chain.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return `${chain} wallet`;
  }
  return PLATFORM_NAMES[link.platform];
}

/** "0x7a3f…c91e": a wallet address is read by its ends; the whole one is the hover. */
const shown = (link: ProfileLink): string =>
  isWallet(link) && link.handle.length > 14
    ? `${link.handle.slice(0, 6)}…${link.handle.slice(-4)}`
    : link.handle;

function AccountLink({ link }: { link: ProfileLink }) {
  // An on-chain identity links no provider page; its name is the whole of it.
  if (link.url === null) return <span>{shown(link)}</span>;
  return (
    <a
      className="link"
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      title={isWallet(link) ? link.handle : undefined}
    >
      {shown(link)}
    </a>
  );
}

const fromNote = (link: ProfileLink): string =>
  link.note ? `A note from ${formatDay(link.note.created_at)}` : 'A note';

/**
 * **A person's X and Bluesky accounts and crypto wallets, for an owner to rule
 * on** (docs/notes/2026-09-26-profile-links-and-principal-social-spec.md,
 * docs/notes/2026-10-01-onchain-intel-arkham-spec.md).
 *
 * Research reads a person's posts, and their wallets, only from an account
 * confirmed here. The
 * sentence that named the account is quoted because the reader decided whose
 * account it is, and the office is the one who can tell. The server sends the
 * block to owners only; the gate here keeps a demo from drawing it.
 */
export function ProfileLinks({
  links,
  subjectId,
}: {
  links: ProfileLinksBlock | null;
  subjectId: string;
}) {
  const mayWrite = useMayWrite(axisOfWrite('profileLink'));
  const rule = useProfileLink(subjectId);
  if (!links || !mayWrite) return null;
  if (links.suggested.length === 0 && links.confirmed.length === 0) return null;

  const working = (anchorId: string, action: string) =>
    rule.isPending && rule.variables?.anchorId === anchorId && rule.variables.action === action;

  return (
    <div className="profile-links">
      {links.suggested.map((link) => (
        <div className="profile-link-suggestion" key={link.anchor_id}>
          <div>
            {isWallet(link) ? (
              <>
                {fromNote(link)} names the {link.chain ? kindOf(link) : 'wallet'}{' '}
                <AccountLink link={link} />.
              </>
            ) : (
              <>
                {fromNote(link)} names <AccountLink link={link} /> on{' '}
                {PLATFORM_NAMES[link.platform]}.
              </>
            )}
          </div>
          {link.note?.sentence ? <div className="src">“{link.note.sentence}”</div> : null}
          <div className="note-actions profile-link-actions">
            <IfWrites needs={axisOfWrite('profileLink')}>
              <WorkingButton
                label={isWallet(link) ? 'Their wallet' : 'Their account'}
                workingLabel="Saving…"
                tiny
                working={working(link.anchor_id, 'confirm')}
                disabled={rule.isPending}
                onClick={() => rule.mutate({ anchorId: link.anchor_id, action: 'confirm' })}
              />
            </IfWrites>
            <IfWrites needs={axisOfWrite('profileLink')}>
              <WorkingButton
                label="Wrong person"
                workingLabel="Saving…"
                variant="act"
                tiny
                working={working(link.anchor_id, 'set_aside')}
                disabled={rule.isPending}
                onClick={() => rule.mutate({ anchorId: link.anchor_id, action: 'set_aside' })}
              />
            </IfWrites>
          </div>
        </div>
      ))}

      {links.confirmed.length > 0 ? (
        <div className="profile-links-confirmed">
          <span className="profile-links-label">Profile links</span>
          {grouped(links.confirmed).map((link) => (
            <span className="profile-link" key={link.anchor_id}>
              {isWallet(link) || link.url === null ? (
                <>
                  {kindOf(link)} <AccountLink link={link} />
                </>
              ) : (
                <a className="link" href={link.url} target="_blank" rel="noopener noreferrer">
                  {link.handle} on {PLATFORM_NAMES[link.platform]}
                </a>
              )}
              {link.note ? (
                <span className="profile-link-origin">
                  {' '}
                  · from a note, {formatDay(link.note.created_at)}
                </span>
              ) : null}
              <IfWrites needs={axisOfWrite('profileLink')}>
                <button
                  type="button"
                  className="linkish flush profile-link-remove"
                  disabled={rule.isPending}
                  onClick={() => rule.mutate({ anchorId: link.anchor_id, action: 'remove' })}
                >
                  {working(link.anchor_id, 'remove') ? 'Removing…' : 'Remove'}
                </button>
              </IfWrites>
            </span>
          ))}
        </div>
      ) : null}

      {rule.isError ? <ErrorLine error={rule.error} /> : null}
    </div>
  );
}
